import React, { useState, useEffect } from 'react';
import api from '../../../api';
import { ShieldAlert, Activity, LogOut, Clock, Smartphone, Globe, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useModal } from '../../../context/ModalContext';

export default function Securite() {
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const { confirm } = useModal();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await api.get('/audit');
        setLogs(response.data);
      } catch (err) {
        if (err.response && err.response.status === 403) {
          setError("Accès refusé. Vous n'avez pas les droits d'Administrateur pour voir cette page.");
        } else {
          setError("Erreur de chargement des logs.");
        }
      } finally {
        setLoading(false);
      }
    };
    
    const fetchSessions = async () => {
      try {
        const res = await api.get('/utilisateurs/me/sessions');
        setSessions(res.data);
      } catch (err) {
        console.error("Erreur de chargement des sessions", err);
      } finally {
        setLoadingSessions(false);
      }
    };

    fetchLogs();
    fetchSessions();
  }, []);

  const handleDisconnectOtherSessions = async () => {
    const isConfirmed = await confirm({
      title: "Déconnecter les autres sessions",
      message: "Êtes-vous sûr de vouloir déconnecter toutes vos autres sessions actives sur d'autres appareils ?",
      type: "warning",
      confirmText: "Déconnecter",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;

    try {
      await api.delete('/utilisateurs/me/sessions');
      setStatusMsg("Les autres sessions ont été déconnectées.");
      setSessions(sessions.filter(s => s.isCurrent));
      setTimeout(() => setStatusMsg(""), 4000);
    } catch (err) {
      console.error("Erreur déconnexion", err);
    }
  };

  const getActionBadge = (action) => {
    switch(action) {
      case 'CREATE': return <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300">CRÉATION</span>;
      case 'UPDATE': return <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300">MODIFICATION</span>;
      case 'DELETE': return <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-300">SUPPRESSION</span>;
      case 'CONVERT': return <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">CONVERSION</span>;
      default: return <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-surface-muted text-primary border border-workspace">{action}</span>;
    }
  };

  const currentSession = sessions.find(s => s.isCurrent);
  const otherSessionsCount = sessions.length > 0 ? sessions.length - (currentSession ? 1 : 0) : 0;

  return (
    <div className="p-6 h-full flex flex-col gap-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-primary flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-info-500" />
            Sécurité & Audit
          </div>
          {statusMsg && (
            <span className="text-sm font-medium flex items-center gap-1.5 text-success-500">
              <CheckCircle2 className="w-4 h-4" />
              {statusMsg}
            </span>
          )}
        </h2>
        <p className="text-sm text-subtle mt-1">Supervisez la sécurité de votre compte et traçez les activités.</p>
      </div>

      {error ? (
        <div className="bg-danger-50 dark:bg-danger-900/20 p-4 rounded-xl flex items-center gap-3 text-danger-800 dark:text-danger-200 border border-danger-200 dark:border-danger-800/50 shadow-sm">
          <ShieldAlert className="w-6 h-6 shrink-0" />
          <p className="font-medium text-sm">{error}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="workspace-card p-6 flex flex-col gap-4">
              <h3 className="text-lg font-semibold text-primary flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-secondary" />
                  Sessions actives
                </span>
                {!loadingSessions && (
                  <span className="text-xs bg-surface-muted border border-workspace px-2 py-1 rounded-md text-subtle font-mono">
                    {sessions.length} totale(s)
                  </span>
                )}
              </h3>
              
              {loadingSessions ? (
                <div className="text-sm text-subtle">Chargement des sessions...</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {currentSession && (
                    <div className="flex items-center justify-between p-3 bg-success-50 dark:bg-success-900/20 rounded-lg border border-success-100 dark:border-success-800/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-success-100 dark:bg-success-800/50 text-success-600 dark:text-success-300 flex items-center justify-center">
                          <Globe className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-success-800 dark:text-success-200">
                            {currentSession.device?.substring(0, 25) || 'Appareil actuel'}
                          </p>
                          <p className="text-xs text-success-600 dark:text-success-400 opacity-90">
                            {currentSession.ipAddress || 'IP Inconnue'} • Session actuelle
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {otherSessionsCount > 0 && (
                    <p className="text-xs text-subtle ml-1">
                      + {otherSessionsCount} autre(s) session(s) active(s)
                    </p>
                  )}
                </div>
              )}

              <button 
                onClick={handleDisconnectOtherSessions} 
                disabled={otherSessionsCount === 0}
                className="btn btn-secondary w-full justify-center mt-auto text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnecter les autres sessions
              </button>
            </div>

            <div className="workspace-card p-6 flex flex-col gap-4 lg:col-span-2">
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Règles d'accès
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Déconnexion automatique</label>
                  <select className="workspace-input w-full md:w-1/2">
                    <option value="15">Après 15 minutes d'inactivité</option>
                    <option value="30">Après 30 minutes d'inactivité</option>
                    <option value="60">Après 1 heure d'inactivité</option>
                    <option value="never">Jamais (Non recommandé)</option>
                  </select>
                </div>
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 rounded-lg text-sm border border-orange-100 dark:border-orange-800/50">
                  <p className="font-semibold mb-1">Restriction par IP</p>
                  <p className="text-xs opacity-90">Limitez l'accès à la plateforme aux seules adresses IP de vos bureaux pour une sécurité maximale.</p>
                  <button className="mt-3 text-sm font-semibold underline decoration-2 underline-offset-2">Configurer les IPs autorisées</button>
                </div>
              </div>
            </div>
          </div>

          <div className="workspace-card overflow-hidden flex flex-col mt-2">
            <div className="p-6 border-b border-workspace flex justify-between items-center">
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                <Activity className="w-5 h-5 text-info-500" />
                Journal d'Audit
              </h3>
            </div>
            
            <div className="overflow-auto max-h-[400px] scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
              {loading ? (
                <div className="p-12 text-center text-subtle">Chargement de l'historique...</div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead className="bg-workspace sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-xs font-semibold text-subtle uppercase tracking-wider border-b border-workspace">Date</th>
                      <th className="px-6 py-3 text-xs font-semibold text-subtle uppercase tracking-wider border-b border-workspace">Utilisateur</th>
                      <th className="px-6 py-3 text-xs font-semibold text-subtle uppercase tracking-wider border-b border-workspace">Action</th>
                      <th className="px-6 py-3 text-xs font-semibold text-subtle uppercase tracking-wider border-b border-workspace">Entité</th>
                      <th className="px-6 py-3 text-xs font-semibold text-subtle uppercase tracking-wider border-b border-workspace">Détails</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-workspace">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-workspace/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-subtle">
                          {format(new Date(log.createdAt), "dd/MM/yyyy HH:mm", { locale: fr })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                          {log.user ? `${log.user.Nom} (${log.user.Role})` : 'Système'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-subtle">
                          <span className="font-medium text-primary">{log.entityType}</span> <br/> 
                          <span className="text-xs opacity-70 font-mono">{log.entityId}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-subtle">
                          <pre className="text-[10px] bg-surface-muted p-2 rounded-lg max-w-xs overflow-x-auto border border-workspace">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-sm text-subtle">
                          Aucun historique disponible.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
